import React from 'react'
import PropTypes from 'prop-types'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import Trans from 'next-translate/Trans'

import styles from './style.module.scss'
import MyImage from '@shared/components/myImage'
import ContactBanner from '@assets/images/contact-banner.png'
import MailIcon from '@assets/images/icon/mail-icon.svg'
import CustomFormGroup from '@shared/components/customForm/customFormGroup'
import CustomInput from '@shared/components/customForm/customInput'
import formStyles from '@assets/scss/components/form.module.scss'
import { FACEBOOK_URL, LINKEDIN_URL, INSTAGRAM_URL, WHATSAPP_URL, TOAST_TYPE, EMAIL, ONLY_NUMBER } from '@shared/constants'
import { INSERT_CONTACT } from '@graphql/cms/cms.query'
import { fireToast } from '@shared/components/toastr/toastr.helper'
import { validationErrors } from '@shared/constants/ValidationErrors'
import CustomFeedback from '@shared/components/customForm/customFeedback'

const InstagramIcon = dynamic(() => import('@shared-components/ctIcons').then((e) => e.InstagramIcon), { ssr: false })
const WhatsappIcon = dynamic(() => import('@shared-components/ctIcons').then((e) => e.WhatsappIcon), { ssr: false })
const LinkedinIcon = dynamic(() => import('@shared-components/ctIcons').then((e) => e.LinkedinIcon), { ssr: false })
const FacebookIcon = dynamic(() => import('@shared-components/ctIcons').then((e) => e.FacebookIcon), { ssr: false })

function ContactUsAdvertise({ typeOfQuery }) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm({ mode: 'onTouched' })

  const [insertContact, { loading }] = useMutation(INSERT_CONTACT, {
    onCompleted: (data) => {
      if (data && data.insertContact) {
        reset({ sName: null, sEmail: null, sPhone: null, sSubject: null, sMessage: null })
        console.log(data)
        fireToast({ message: data.insertContact.sMessage, type: TOAST_TYPE.Success })
      }
    }
  })

  function onSubmit(formData) {
    const data = { ...formData, eQueryType: 'ad' }
    insertContact({ variables: { input: { contactInput: data } } })
  }

  return (
    <div className={`${styles.contactWrapper}`}>
      <div className={`${styles.contactHeading} text-center`}>
        <MyImage className={`${styles.contactBanner}`} alt="bg" src={ContactBanner} />
        <h2 className="mb-0 font-medium">Contact Us</h2>
      </div>
      <div className={`${styles.contactInnerWrapper} p-3`}>
        <Row>
          <Col md={7}>
            <div className={`${styles.contactform} p-3`}>
              <Row>
                <Col md={6}>
                  <CustomFormGroup className={formStyles.formGroup}>
                    <Form.Label className={`${formStyles.label}`}>
                      <Trans i18nKey="common:FullName" />*
                    </Form.Label>
                    <CustomInput
                      className={`${formStyles.formControl} ${errors.sName && formStyles.hasError}`}
                      type="text"
                      name="sName"
                      register={register('sName', {
                        required: validationErrors.required
                      })}
                    />
                    {errors.sName && (
                      <CustomFeedback message={errors.sName.message} type="invalid" className={`${formStyles.invalidFeedback}`} />
                    )}
                  </CustomFormGroup>
                </Col>
                <Col md={6}>
                  <CustomFormGroup className={formStyles.formGroup}>
                    <Form.Label className={`${formStyles.label}`}>
                      <Trans i18nKey="common:Email" />*
                    </Form.Label>
                    <CustomInput
                      className={`${formStyles.formControl} ${errors.sEmail && formStyles.hasError}`}
                      type="text"
                      name="sEmail"
                      register={register('sEmail', {
                        required: validationErrors.required,
                        pattern: { value: EMAIL, message: validationErrors.email },
                        validate: (value) => setValue('sEmail', value.toLowerCase())
                      })}
                    />
                    {errors.sEmail && (
                      <CustomFeedback message={errors.sEmail.message} type="invalid" className={`${formStyles.invalidFeedback}`} />
                    )}
                  </CustomFormGroup>
                </Col>
                <Col md={6}>
                  <CustomFormGroup className={formStyles.formGroup}>
                    <Form.Label className={`${formStyles.label}`}>
                      <Trans i18nKey="common:PhoneNo" />*
                    </Form.Label>
                    <CustomInput
                      className={`${formStyles.formControl} ${errors.sPhone && formStyles.hasError}`}
                      type="number"
                      name="sPhone"
                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                      register={register('sPhone', {
                        required: validationErrors.required,
                        pattern: { value: ONLY_NUMBER, message: validationErrors.number },
                        maxLength: { value: 10, message: validationErrors.number },
                        minLength: { value: 10, message: validationErrors.number }
                      })}
                    />
                    {errors.sPhone && (
                      <CustomFeedback message={errors.sPhone.message} type="invalid" className={`${formStyles.invalidFeedback}`} />
                    )}
                  </CustomFormGroup>
                </Col>
                <Col md={6}>
                  <CustomFormGroup className={formStyles.formGroup}>
                    <Form.Label className={`${formStyles.label}`}>
                      <Trans i18nKey="common:Subject" />*
                    </Form.Label>
                    <CustomInput
                      className={`${formStyles.formControl} ${errors.sSubject && formStyles.hasError}`}
                      type="text"
                      name="sSubject"
                      register={register('sSubject', {
                        required: validationErrors.required
                      })}
                    />
                    {errors.sSubject && (
                      <CustomFeedback message={errors.sSubject.message} type="invalid" className={`${formStyles.invalidFeedback}`} />
                    )}
                  </CustomFormGroup>
                </Col>
                <Col md={12}>
                  <CustomFormGroup className={formStyles.formGroup}>
                    <Form.Label className={`${formStyles.label}`}>
                      <Trans i18nKey="common:Message" />*
                    </Form.Label>
                    <CustomInput
                      className={`${formStyles.formControl} ${formStyles.formTextarea} ${errors.sMessage && formStyles.hasError}`}
                      as="textarea"
                      name="sMessage"
                      register={register('sMessage', {
                        required: validationErrors.required
                      })}
                    />
                    {errors.sMessage && (
                      <CustomFeedback message={errors.sMessage.message} type="invalid" className={`${formStyles.invalidFeedback}`} />
                    )}
                  </CustomFormGroup>
                </Col>
              </Row>
              <Button className="theme-btn" onClick={handleSubmit(onSubmit)} variant="primary" type="submit" disabled={loading}>
                <Trans i18nKey="common:Submit" />
                {loading && <Spinner className="ms-2 align-middle" animation="border" />}
              </Button>
            </div>
          </Col>
          <Col md={5} className="align-self-center">
            <div className={`${styles.contactInforamtion} p-md-3`}>
              <div className={`${styles.contactInforamtionwrapper} p-3`}>
                <h4 className="mb-2 text-uppercase">CONTACT INFORMATION</h4>
                <div className={`${styles.contactInfo} d-flex align-items-center gap-2 mb-2`}>
                  <div className={`${styles.icon}`}>
                    <MyImage src={MailIcon} alt="mail" width="24" height="24" />
                  </div>
                  <Link href="mailto:contact@crictracker.com">contact@crictracker.com</Link>
                </div>
                <ul className={`${styles.socialMenu} d-flex text-uppercase align-items-center mb-0`}>
                  <li>
                    <a className="d-block" href={INSTAGRAM_URL} target="_blank" rel="nofollow noreferrer">
                      <InstagramIcon />
                    </a>
                  </li>
                  <li>
                    <a className="d-block" href={WHATSAPP_URL} target="_blank" rel="nofollow noreferrer">
                      <WhatsappIcon />
                    </a>
                  </li>
                  <li>
                    <a className="d-block" href={LINKEDIN_URL} target="_blank" rel="nofollow noreferrer">
                      <LinkedinIcon />
                    </a>
                  </li>
                  <li>
                    <a className="d-block" href={FACEBOOK_URL} target="_blank" rel="nofollow noreferrer">
                      <FacebookIcon />
                    </a>
                  </li>
                </ul>
              </div>
              <div className={`${styles.contactInforamtionwrapper} p-3`}>
                <h4 className="mb-2 text-uppercase">ADVERTISEMENT QUERY</h4>
                <p className="mb-2">For advertisement query, kindly reach out to us on the below email</p>
                <div className={`${styles.contactInfo} d-flex align-items-center gap-2 mb-2`}>
                  <div className={`${styles.icon}`}>
                    <MyImage src={MailIcon} alt="mail" width="24" height="24" />
                  </div>
                  <Link href="mailto:advertise@crictracker.com">advertise@crictracker.com</Link>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}
ContactUsAdvertise.propTypes = {
  typeOfQuery: PropTypes.array
}
export default ContactUsAdvertise
